import { Injectable } from '@angular/core';
import { SharedConstants } from '@formbird/types';
import {
  UtilConflict, UtilDocument, UtilSequenceProcess, UtilType,
  DocumentIdValidator
} from '@formbird/shared';
import { DocumentUpdateService } from './document-update.service';
import { MandatoryFieldService } from './mandatory-field.service';
import { DataService } from '../data/data.service';
import { LoggedInUserService } from '../user/logged-in-user.service';
import { NotificationService } from '../notification/notification.service';
import { DocumentService } from './document.service';
import { UnsavedDocumentService } from './unsaved-document.service';
import { CurrentDocumentService } from './current-document.service';
import { CurrentChildDocumentService } from './current-child-document.service';
import { ModifiedFieldService } from './modified-field.service';
import { ValidationService } from '../validation/validation.service';
import { ClientSystemHeaderService } from './client-system-header.service';
import { ConfigService } from '../config/config.service';
import { BroadcastService } from '../broadcast/broadcast.service';
import { ClientAccessService } from '../access/client-access.service';
import { ChangedDocumentService } from './changed-document.service';

import { cloneDeep } from 'lodash';
import { FinaliseValueFieldService } from './finalise-value-field.service';

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class DocumentSaveService {

  private savedDocs;

  documentData: any;

  constructor(
    private dataService: DataService,
    private loggedInUserService: LoggedInUserService,
    private notificationService: NotificationService,
    private documentService: DocumentService,
    private unsavedDocumentService: UnsavedDocumentService,
    private currentDocumentService: CurrentDocumentService,
    private currentChildDocumentService: CurrentChildDocumentService,
    private mandatoryFieldService: MandatoryFieldService,
    private modifiedFieldService: ModifiedFieldService,
    private validationService: ValidationService,
    private clientSystemHeaderService: ClientSystemHeaderService,
    private documentUpdateService: DocumentUpdateService,
    private configService: ConfigService,
    private broadcastService: BroadcastService,
    private clientAccessService: ClientAccessService,
    private changedDocumentService: ChangedDocumentService,
    private finaliseValueService: FinaliseValueFieldService
  ) {
    this.currentDocumentService.documentData$.subscribe(data => {
      this.documentData = data;
    });
  }

  private loadOverrideTemplate(document) {

    const documentData = this.currentDocumentService.getDocumentData();

    if (documentData.overrideTemplateId && documentData.document.documentId === document.documentId) {

      return documentData.overrideTemplateId;
    }
  }

  private loadTemplate(document) {

    const overrideTemplateId = this.loadOverrideTemplate(document);
    const templateId = overrideTemplateId ? overrideTemplateId : document.systemHeader.templateId;

    return this.unsavedDocumentService.getTemplateOnly(templateId);
  }

  private doPreSaveData(document, unsavedDocumentListId) {

    const docBean = cloneDeep(document);

    const template = this.loadTemplate(docBean);

    this.finaliseValueService.finaliseFieldValues(docBean, template);

    if (!docBean.systemHeader) {
      docBean.systemHeader = {};
    }

    if (!docBean.systemHeader.systemType) {
      docBean.systemHeader.systemType = SharedConstants.SYSTEM_TYPE_DOCUMENT;
    }

    if (UtilDocument.isNew(docBean) && !docBean.systemHeader.templateId) {
      docBean.systemHeader.templateId = template.documentId;
    }

    this.clientAccessService.writeKeysToDocument(docBean, template);

    const overrideTemplateId = this.loadOverrideTemplate(document);
    if (overrideTemplateId) {
      docBean.systemHeader.createdWith = overrideTemplateId;
    } else {
      docBean.systemHeader.createdWith = document.systemHeader.templateId;
    }

    const docContext = {} as any;
    docContext.template = template;
    docContext.templateId = document.systemHeader.templateId;
    docContext.unsavedDocumentListId = unsavedDocumentListId;

    return this.preSaveValidation(docBean, docContext);
  }

  private preSaveValidation(docBean, docContext) {

    const _self = this;

    return new Promise((resolve, reject) => {

      // const formParameters = _self.$rootScope.formParameters;
      const formParameters = {};

      _self.validationService.onPreSaveDocument(formParameters, docBean, docContext, function(error, result) {

        if (error) {
          reject(error);
        } else {

          _self.validationService.broadcastValidationEvent();
          resolve(docBean);
        }
      });
    });

  }

  private resetCleanStateDocument(doc) {

    this.documentService.resetDisableSaveFieldToDoc(doc);

    this.documentService.resetOriginalDocument(doc);

    this.currentDocumentService.resetUndoRedo();

    this.changedDocumentService.resetChangeDocument();
  }

  private performPostSaveEach(doc) {

    this.notificationService.success('Saved successfully');

    this.modifiedFieldService.removeModifiedDocumentFields(doc.documentId);

    this.resetCleanStateDocument(doc);

    // Put doc into an array due to avoid changing the handler in custom component
    this.broadcastService.broadcast('document:updated', [doc]);
  }

  private processSaveEach(modifiedDocument, unsavedDocumentListId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      const result = DocumentIdValidator.validateDocumentId(modifiedDocument.documentId,
        _self.configService.clientConfig().documentIdRules);

      if (!result.valid) {
        reject(new Error(result.reason));
      }

      _self.reloadDocumentAndApplyUpdates(modifiedDocument, unsavedDocumentListId).then(
        function successFunc(newModifiedDoc) {

          _self.doPreSaveData(newModifiedDoc, unsavedDocumentListId).then(
            function preSaveSuccessFunc(processedDoc: any) {

              if (processedDoc) {
                logger.info('PreSave done for document: ' + processedDoc.documentId);

                _self.doSaveEach(processedDoc, unsavedDocumentListId).then(
                  function(doc) {

                    _self.performPostSaveEach(doc);
                    resolve(doc);

                  },
                  function(err) {

                    reject(err);
                  }
                );

              } else {

                const msg = 'There is error in PreSave.';
                logger.error(msg);

                reject(new Error(msg));
              }
            },

            function errorFunc(error) {
              logger.info('Saving data error...' + JSON.stringify(error));
              reject(error);
            }
          );
        },

        function errorFunc(err) {
          logger.info('Saving data error...' + JSON.stringify(err));
          reject(err);
        }
      );

    });

  }

  private doSaveEach(docBean, unsavedDocumentListId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      if (docBean) {
        logger.info('Saving document: ' + docBean.documentId);

        const tpl = _self.documentService.getTemplateFromDoc(docBean);
        let originalDoc = _self.documentService.getExistingDocument(docBean.documentId);

        if (!originalDoc && _self.configService.clientConfig().useSaveConflictResolution) {
          originalDoc = _self.documentService.getExistingDocument(docBean.systemHeader.previousVersionId);

        } else if (!originalDoc) {
          originalDoc = _self.documentService.getExistingDocument(docBean.systemHeader.versionId);
        }

        const isCreationMode = UtilDocument.isNew(docBean);
        if (isCreationMode) {

          _self.dataService.insert(docBean).then(
            function(savedDoc) {

              _self.savedDocs.push(savedDoc);

              resolve(savedDoc);

              logger.info('Document inserted: ' + savedDoc.documentId);
            },
            function(err) {
              reject(err);
            });

        } else {

          if (!_self.clientSystemHeaderService.updateSystemHeader(docBean, tpl, originalDoc)) {
            reject(new Error('Error writing system header'));

          } else {

            const options = {
              templateId: originalDoc.templateId,
              makeCurrentVersion: _self.unsavedDocumentService.getDocumentOptionKey(unsavedDocumentListId,
                docBean.documentId, SharedConstants.MAKE_CURRENT_VERSION_KEY),
              importPreviousVersion: _self.unsavedDocumentService.getDocumentOptionKey(unsavedDocumentListId,
                docBean.documentId, SharedConstants.IMPORT_PREVIOUS_VERSION)
            };

            _self.dataService.jsonPatchUpdate(originalDoc, docBean, options).then(
              function(savedDoc: any) {
                _self.savedDocs.push(savedDoc);
                resolve(savedDoc);

                logger.info('Document updated: ' + savedDoc.documentId);
              },
              function(err) {
                reject(err);
              });
          }
        }

      } else {
        resolve();
      }

    });

  }

  setFlags(dirtyFlag, spinnerFlag, saveFlag) {

    if (spinnerFlag !== undefined) {
      this.modifiedFieldService.setShowSpinner(spinnerFlag);
    }

    if (saveFlag !== undefined) {
      this.modifiedFieldService.setSaveFlag(saveFlag);
    }

    if (saveFlag) {
      // Saving means the update on form is allowed,
      // so set updateAllowed flag to true in order to show the dirty again in case saving errors
      this.modifiedFieldService.setUpdateAllowed(true);
    }

    if (dirtyFlag !== undefined) {
      this.modifiedFieldService.setDirty(dirtyFlag);
    }
  }

  private doSave(unsavedDocumentListId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      try {
        // check mandatory fields in all documents getUnsavedDocuments will throw an exception
        // if the unsavedDocumentListId is unknown
        const unsavedDocuments = _self.unsavedDocumentService.getUnsavedDocuments(unsavedDocumentListId);
        const unsavedTemplates = _self.unsavedDocumentService.getUnsavedTemplates(unsavedDocumentListId);

        _self.mandatoryFieldService.isMandatoryAnswered(unsavedDocuments, unsavedTemplates).then(
          function successFunc() {

            // 11003: only save modified documents. Don't save the parent if only a child has changed
            const modifiedDocuments = _self.modifiedFieldService.getModifiedDocuments(unsavedDocumentListId);

            if (modifiedDocuments && modifiedDocuments.length) {
              _self.savedDocs = [];

              // save documents sequentially so that all document saves aren't sent to the server at the
              // same time. See the notes of Mantis 11213 for further details
              UtilSequenceProcess.processList(modifiedDocuments, (modifiedDocument) => {
                return _self.processSaveEach(modifiedDocument, unsavedDocumentListId);
              }).then(
                function allPromiseSuccessFunc() {

                  if (_self.savedDocs && _self.savedDocs.length > 0) {
                    resolve(_self.savedDocs);

                  } else {
                    reject(new Error('No results returned from save'));
                  }
                },
                function errorFunc(err) {
                  logger.info('Saving data got errors...' + JSON.stringify(err));

                  reject(err);
                });

            } else {
              logger.info('Exit saving because there is no modified document.');
              // resolve with no document because there's no save
              resolve();
            }

          }, function errorFunc(err) {
            reject(err);
          });

      } catch (e) {
        // pass through the error with no additional messages so that the error is
        // nicely formatted for display
        reject(new Error(e));
      }

    });

  }

  private reloadDocumentAndApplyUpdates(modifiedDoc, unsavedDocumentListId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      var isMakingCurVer = _self.unsavedDocumentService.getDocumentOptionKey(
        unsavedDocumentListId, modifiedDoc.documentId, SharedConstants.MAKE_CURRENT_VERSION_KEY);

      if (UtilDocument.isNew(modifiedDoc) || UtilDocument.isPreviousVersion(modifiedDoc) || isMakingCurVer) {

        resolve(modifiedDoc);
      } else {
        const documentId = modifiedDoc.documentId;

        let oldOriginalDoc = _self.documentService.getExistingDocument(documentId);
        if (!oldOriginalDoc) {
          oldOriginalDoc = _self.documentService.getExistingDocument(modifiedDoc.systemHeader.versionId);
        }

        if (oldOriginalDoc) {

          // load the latest version from DB first
          _self.dataService.getDocument(documentId).then(
            function successFunc(latestDoc) {

              let diffs = UtilConflict.getDiffs(oldOriginalDoc, latestDoc);
              if (diffs.length) {
                // get the changed fields by rules and user on form
                diffs = UtilConflict.getDiffs(oldOriginalDoc, modifiedDoc);

                // update the latest version to current original version
                _self.documentService.resetOriginalDocument(latestDoc);

                // create a new copied version from the latest one
                const newModifiedDoc = cloneDeep(latestDoc);

                // apply the changes by rules and user on form to the new document
                if (diffs.length) {
                  diffs.forEach(function(diff) {
                    newModifiedDoc[diff.fieldName] = modifiedDoc[diff.fieldName];
                  });
                }

                const template = _self.loadTemplate(newModifiedDoc);
                if (template && template.components && template.components.length) {

                  template.components.forEach(function(component) {

                    if (!UtilType.isDefined(newModifiedDoc[component.name])) {

                      if (UtilType.isDefined(component.defaultValue)) {

                        newModifiedDoc[component.name] = component.defaultValue;
                      }
                    }
                  });
                }

                // set the new changed version to unsaved list
                _self.unsavedDocumentService.setDocument(unsavedDocumentListId, newModifiedDoc);

                resolve(newModifiedDoc);
              } else {
                resolve(modifiedDoc);
              }
            },
            function errorFunc(err) {
              reject(err);
            }
          );
        } else {
          resolve(modifiedDoc);
        }
      }
    });

  }

  public save(unsavedDocumentListId) {

    const _self = this;

    return new Promise((resolve, reject) => {

      if (!unsavedDocumentListId) {
        reject(new Error('Unsaved document list id not provided in save'));

      } else if (_self.modifiedFieldService.isSaving()) {
        reject(new Error('Save is already in progress'));

      } else {

        _self.setFlags(false, true, true);

        const unsavedDocuments = _self.modifiedFieldService.getModifiedDocuments(unsavedDocumentListId);

        _self.doSave(unsavedDocumentListId).then(
          async function doSaveSuccess(docs: []) {
            // reset temporary array of saved documents
            _self.savedDocs = null;

            if (docs) {
              for (let id = 0; id < docs.length; id++) {
                const doc: any = cloneDeep(docs[id]);

                // Remove offline info before applying the changes
                if (doc.systemHeader) {
                  delete doc.systemHeader.offlineDetails;
                }

                await _self.documentUpdateService.updateFormDocument(unsavedDocuments, doc);
              }
            }

            _self.setFlags(false, false, false);

            resolve(docs);
          },
          function errorFunc(err) {
            _self.setFlags(true, false, false);

            reject(err);
          });
      }
    });

  }

}
